/**
 * Interview view-model (R3.3, R6.1, R8.4, R9.1). Framework-agnostic state the UI
 * renders: the running transcript, the current prompt, the selected locale and
 * input modality, and a resume recap banner. Kept pure so UI behavior is
 * testable without a browser; a thin React/DOM layer binds to it.
 */
import { ServerMessage } from './socket-client';

export type InputModality = 'text' | 'voice';

export interface TranscriptEntry {
  role: 'assistant' | 'user';
  text: string;
}

export interface InterviewViewState {
  transcript: TranscriptEntry[];
  currentPrompt: string | null;
  recap: string | null;
  locale: string;
  modality: InputModality;
  complete: boolean;
  summary: string | null;
}

export class InterviewViewModel {
  private state: InterviewViewState;

  constructor(locale = 'en') {
    this.state = {
      transcript: [],
      currentPrompt: null,
      recap: null,
      locale,
      modality: 'text',
      complete: false,
      summary: null,
    };
  }

  get(): InterviewViewState {
    return this.state;
  }

  setRecap(recap: string): void {
    this.state = { ...this.state, recap };
  }

  setLocale(locale: string): void {
    this.state = { ...this.state, locale };
  }

  setModality(modality: InputModality): void {
    this.state = { ...this.state, modality };
  }

  /** Record a user turn in the transcript. */
  recordUserTurn(text: string): void {
    this.state = {
      ...this.state,
      transcript: [...this.state.transcript, { role: 'user', text }],
    };
  }

  /** Apply a server message to the view state. */
  apply(msg: ServerMessage): void {
    if (msg.type === 'prompt') {
      this.state = {
        ...this.state,
        currentPrompt: msg.prompt,
        transcript: [...this.state.transcript, { role: 'assistant', text: msg.prompt }],
      };
    } else if (msg.type === 'complete') {
      this.state = {
        ...this.state,
        currentPrompt: null,
        complete: true,
        summary: msg.summary,
        transcript: [...this.state.transcript, { role: 'assistant', text: msg.summary }],
      };
    } else if (msg.type === 'transcript') {
      // What Nova Sonic heard for a voice turn: shown as the user's answer.
      this.state = {
        ...this.state,
        transcript: [...this.state.transcript, { role: 'user', text: msg.text }],
      };
    } else if (msg.type === 'reprompt') {
      // Unintelligible voice capture: show the reprompt and offer text (R8.4).
      this.state = {
        ...this.state,
        currentPrompt: msg.message,
        modality: 'text',
        transcript: [...this.state.transcript, { role: 'assistant', text: msg.message }],
      };
    }
  }
}
